import { chain, isArray, isEmpty } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { QueryOperator } from 'src/constants';

const SPLIT_BY_OPERATOR = /(?<!\\)[\|\&]/g; // eslint-disable-line no-useless-escape

export interface DefaultQueryProps {
  [key: string]: string[];
}

export const useParamQuery = (defaultQuery?: DefaultQueryProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currPage, setCurrPage] = useState(1);

  // list of all active URL params
  const activeParams = useMemo(() => {
    const params = {} as DefaultQueryProps;

    for (const [key, value] of searchParams) {
      params[key] = chain(value).split(SPLIT_BY_OPERATOR).compact().value();
    }

    return params;
  }, [searchParams]);

  // list of all active filters (excludes pagination and sorting params)
  const activeFilters = useMemo(() => {
    const denylist = ['sort', 'pageSize', 'page', 'count'];

    const filters = {} as DefaultQueryProps;

    for (const [key, value] of searchParams) {
      if (!denylist.includes(key)) {
        filters[key] = chain(value).split(SPLIT_BY_OPERATOR).compact().value();
      }
    }

    return filters;
  }, [searchParams]);

  const allActiveFilters = useMemo(() => {
    const filters = {} as DefaultQueryProps;

    for (const [key, value] of searchParams) {
      filters[key] = chain(value).split(SPLIT_BY_OPERATOR).compact().value();
    }

    return filters;
  }, [searchParams]);

  useEffect(() => {
    if (!isEmpty(defaultQuery)) {
      const keys = Object.keys(defaultQuery);

      for (const key of keys) {
        if (!searchParams.get(key)) {
          searchParams.set(key, defaultQuery[key].join(QueryOperator.OR));
          setSearchParams(searchParams, { replace: true });
        }
      }
    }
  }, [defaultQuery, searchParams, setSearchParams]);

  const getParamsValue = useCallback(
    (
      paramsName: string,
      encodeFn = encodeURIComponent,
      decodeFn = decodeURIComponent,
    ) => {
      const paramValue = searchParams.get(paramsName);
      const separateByPipe = paramValue?.split(/\|/) || [];
      const encodedParams = separateByPipe.map((v) => encodeFn(v)).join('|');

      return chain(encodedParams)
        .split(SPLIT_BY_OPERATOR)
        .compact()
        .value()
        .map((v) => decodeFn(v));
    },
    [searchParams],
  );

  const onChangeParams = useCallback(
    (
      key: string,
      value: string | string[],
      replace = false,
      operator: QueryOperator = QueryOperator.OR,
      resetPage = true,
    ) => {
      if (key !== 'page' && resetPage) {
        searchParams.delete('page');
        setCurrPage(1);
      }
      if (isArray(value) && isEmpty(value)) {
        searchParams.delete(key);
      } else {
        const joinedValue = isArray(value) ? value.join(operator) : value;
        searchParams.set(key, joinedValue);
      }
      setSearchParams(searchParams, { replace });
    },
    [searchParams, setSearchParams],
  );

  const onChangeMultipleParams = useCallback(
    (
      params: Record<string, string | string[]>,
      replace = false,
      operator: QueryOperator = QueryOperator.OR,
    ) => {
      for (const [key, paramValue] of Object.entries(params)) {
        if (isArray(paramValue) && isEmpty(paramValue)) {
          searchParams.delete(key);
          continue;
        }

        const nextParamValue = isArray(paramValue)
          ? paramValue.join(operator)
          : paramValue;

        searchParams.set(key, nextParamValue);
      }

      setSearchParams(searchParams, { replace });
    },
    [searchParams, setSearchParams],
  );

  const onChangeParamsKey = useCallback(
    (oldKey: string, newKey: string, replace = false) => {
      const values = searchParams.getAll(oldKey);
      if (!isEmpty(values)) {
        searchParams.delete(oldKey);
        searchParams.set(newKey, values.join(QueryOperator.OR));
        setSearchParams(searchParams, { replace });
      }
    },
    [searchParams, setSearchParams],
  );

  return {
    activeParams,
    activeFilters,
    allActiveFilters,
    onChangeParams,
    onChangeMultipleParams,
    getParamsValue,
    currPage,
    setCurrPage,
    onChangeParamsKey,
  };
};
